import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    IconButton,
    Paper,
    TextField,
    Divider,
    alpha,
    useTheme,
    useMediaQuery,
    Zoom,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import dayjs from 'dayjs';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Close, ContentCopy, AccessTime, CalendarToday } from '@mui/icons-material';
import { toast } from 'react-toastify';

const CustomCalendarHeader = (props: any) => {
    const { currentMonth, onMonthChange, minDate, maxDate } = props;

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const currentYear = currentMonth.year();
    const currentMonthIndex = currentMonth.month();

    const startYear = minDate ? dayjs(minDate).year() : currentYear - 10;
    const endYear = maxDate ? dayjs(maxDate).year() : currentYear + 10;

    const years = [];
    for (let i = startYear; i <= endYear; i++) {
        years.push(i);
    }

    const handleMonthChange = (event: any) => {
        onMonthChange(currentMonth.month(event.target.value), 'left');
    };

    const handleYearChange = (event: any) => {
        onMonthChange(currentMonth.year(event.target.value), 'left');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Select
                    size="small"
                    value={currentMonthIndex}
                    onChange={handleMonthChange}
                    sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': { py: 0.5, px: 1 },
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' }
                    }}
                >
                    {months.map((month, index) => (
                        <MenuItem key={month} value={index}>{month}</MenuItem>
                    ))}
                </Select>
                <Select
                    size="small"
                    value={currentYear}
                    onChange={handleYearChange}
                    sx={{
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': { py: 0.5, px: 1 },
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' }
                    }}
                >
                    {years.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                </Select>
            </Box>
            <Box>
                <IconButton size="small" onClick={() => onMonthChange(currentMonth.subtract(1, 'month'), 'right')}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onMonthChange(currentMonth.add(1, 'month'), 'left')}>
                    <ChevronRight fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
};

interface Booking {
    id: number;
    date: string;
    service: string;
    startTime: string;
    endTime: string;
    _id?: string;
    serviceId?: string;
    location?: string;
    ageRange?: number[];
    description?: string;
    personRequired?: number;
    language?: string[];
    extraDetails?: any;
    attachment?: any[];
    hours?: number;
}

interface DuplicateModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (fromDate: string, toDate: string, fromTime: string, toTime: string) => void;
    sourceBooking: Booking | null;
    defaultDate?: string;
}

const DuplicateModal: React.FC<DuplicateModalProps> = ({
    open,
    onClose,
    onConfirm,
    sourceBooking,
    defaultDate = ''
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [fromDate, setFromDate] = useState<string>(defaultDate);
    const [toDate, setToDate] = useState<string>(defaultDate);
    const [fromTime, setFromTime] = useState<string>('09:00');
    const [toTime, setToTime] = useState<string>('11:00');

    // Check if service is cleaning or housekeeping
    const isFixedService = React.useMemo(() => {
        if (!sourceBooking?.service) return false;
        const lower = sourceBooking.service.toLowerCase();
        return ['cleaning', 'housekeeping', 'cleaner', 'maid'].some(k => lower.includes(k));
    }, [sourceBooking]);

    // Calculate original duration in minutes
    const fixedDurationMinutes = React.useMemo(() => {
        if (!sourceBooking || !sourceBooking.startTime || !sourceBooking.endTime) return 120; // Default 2h
        const [startH, startM] = sourceBooking.startTime.split(':').map(Number);
        const [endH, endM] = sourceBooking.endTime.split(':').map(Number);
        let startMinutes = startH * 60 + startM;
        let endMinutes = endH * 60 + endM;
        if (endMinutes < startMinutes) endMinutes += 24 * 60; // Handle overnight default case
        return endMinutes - startMinutes;
    }, [sourceBooking]);

    useEffect(() => {
        if (open) {
            setFromDate(defaultDate || '');
            setToDate(defaultDate || '');

            if (sourceBooking) {
                const safeLimit = dayjs().add(2, 'hour');
                // Check if source time is valid for the default date
                const sourceStart = dayjs(`${defaultDate || dayjs().format('YYYY-MM-DD')} ${sourceBooking.startTime}`);

                if (sourceStart.isBefore(safeLimit)) {
                    // Find next safe slot
                    const nextSafe = safeLimit.minute() > 0 ? safeLimit.add(1, 'hour').startOf('hour') : safeLimit.startOf('hour');
                    const nextSafeStr = nextSafe.format('HH:mm');
                    setFromTime(nextSafeStr);

                    // Use fixed duration if applicable, else default 2h
                    const durationToAdd = isFixedService ? fixedDurationMinutes : 120;
                    const toD = nextSafe.add(durationToAdd, 'minute');
                    setToTime(toD.format('HH:mm'));
                } else {
                    setFromTime(sourceBooking.startTime || '09:00');
                    setToTime(sourceBooking.endTime || '11:00');
                }
            }
        }
    }, [open, defaultDate, sourceBooking, isFixedService, fixedDurationMinutes]);

    // Validate/Reset time when Date changes
    useEffect(() => {
        if (!fromDate) return;

        const safeLimit = dayjs().add(2, 'hour');

        // Ensure we format valid HH:mm correctly for comparison
        // Check current selected time
        // Note: fromTime might be formatted as HH:00 or HH:mm
        const currentSelected = dayjs(`${fromDate} ${fromTime}`);

        if (currentSelected.isBefore(safeLimit)) {
            // Find next valid time for this date
            // Loop to find first avail
            for (let h = 0; h < 24; h++) {
                const t = `${String(h).padStart(2, '0')}:00`;
                const dt = dayjs(`${fromDate} ${t}`);
                if (dt.isAfter(safeLimit) || dt.isSame(safeLimit, 'minute')) {
                    setFromTime(t);

                    // Set to Time based on fixed duration or default 2h
                    const durationToAdd = isFixedService ? fixedDurationMinutes : 120;
                    const toD = dt.add(durationToAdd, 'minute');
                    setToTime(toD.format('HH:mm')); // Use minutes in case of fractional hours
                    return;
                }
            }
            // If no slots today
            setFromTime('');
            setToTime('');
        }
    }, [fromDate]);

    const handleFromTimeChange = (newFromTime: string) => {
        setFromTime(newFromTime);
        // Auto-update toTime
        const [hours, minutes] = newFromTime.split(':').map(Number);

        let duration = isFixedService ? fixedDurationMinutes : 120;

        // Calculate new end time using minutes to support fractional durations
        const startTotalMinutes = hours * 60 + minutes;
        let endTotalMinutes = startTotalMinutes + duration;

        // Handle overflow 24h
        if (endTotalMinutes >= 24 * 60) {
            endTotalMinutes -= 24 * 60;
        }

        const newEndH = Math.floor(endTotalMinutes / 60);
        const newEndM = endTotalMinutes % 60;

        const newToTime = `${String(newEndH).padStart(2, '0')}:${String(newEndM).padStart(2, '0')}`;
        setToTime(newToTime);
    };

    const handleConfirm = () => {
        if (fromDate && toDate && fromTime && toTime) {
            // Final validation
            const safeLimit = dayjs().add(2, 'hour');
            const selectedStart = dayjs(`${fromDate} ${fromTime}`);

            if (selectedStart.isBefore(safeLimit)) {
                toast.error("Selected time is invalid (must be at least 2 hours from now).");
                return;
            }

            onConfirm(fromDate, toDate, fromTime, toTime);
            onClose();
        }
    };

    const todayStr = dayjs().format('YYYY-MM-DD');

    const getMinSafeHour = () => {
        const now = dayjs();
        let safeHour = now.hour() + 2;
        if (now.minute() > 0) safeHour++;
        return safeHour;
    };

    const minSafeHour = getMinSafeHour();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            TransitionComponent={Zoom}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 4,
                    backgroundColor: alpha('#fff', 0.98),
                    backdropFilter: 'blur(20px)',
                    border: '2px solid',
                    borderColor: alpha('#F475A4', 0.15),
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 3.5, px: 3.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 16px rgba(244, 117, 164, 0.3)'
                        }}
                    >
                        <ContentCopy sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={800} sx={{
                        fontSize: { xs: '1.2rem', sm: '1.35rem' },
                        background: 'linear-gradient(135deg, #F475A4 0%, #D45E8C 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Duplicate Booking
                    </Typography>
                </Stack>
            </DialogTitle>

            <Divider sx={{ borderColor: alpha('#F475A4', 0.1) }} />

            <DialogContent sx={{ pt: 3.5, px: 3.5 }}>
                <Stack spacing={3}>
                    {sourceBooking && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(186, 104, 200, 0.05) 100%)',
                                borderRadius: 2.5,
                                border: '2px solid',
                                borderColor: alpha('#9C27B0', 0.15)
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block" fontWeight={700} gutterBottom sx={{ fontSize: '0.75rem', mb: 1 }}>
                                Duplicating Booking
                            </Typography>
                            <Typography variant="h6" fontWeight={800} color="primary" gutterBottom sx={{ mb: 1 }}>
                                {sourceBooking.service}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    {sourceBooking.startTime} - {sourceBooking.endTime}
                                </Typography>
                            </Stack>
                        </Paper>
                    )}

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack spacing={2}>
                            <MobileDatePicker
                                label="From Date"
                                value={fromDate ? dayjs(fromDate) : null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setFromDate(newValue.format('YYYY-MM-DD'));
                                        // Auto-update toDate if it's before fromDate or empty
                                        if (!toDate || dayjs(toDate).isBefore(newValue)) {
                                            setToDate(newValue.format('YYYY-MM-DD'));
                                        }
                                    }
                                }}
                                format="DD-MM-YYYY"
                                minDate={dayjs()}
                                maxDate={dayjs().add(2, 'year')}
                                slots={{ calendarHeader: CustomCalendarHeader }}
                                slotProps={{
                                    toolbar: { hidden: true },
                                    textField: {
                                        fullWidth: true,
                                        size: isMobile ? "small" : "medium",
                                        autoComplete: 'off',
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ mr: -1 }}>
                                                    <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                cursor: "pointer",
                                                borderRadius: 2.5,
                                                backgroundColor: '#fff',
                                                '& fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: alpha('#F475A4', 0.15),
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: alpha('#F475A4', 0.3),
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#F475A4',
                                                },
                                                '& input': { cursor: 'pointer' }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontWeight: 600
                                            }
                                        }
                                    }
                                }}
                            />
                            <MobileDatePicker
                                label="To Date"
                                value={toDate ? dayjs(toDate) : null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setToDate(newValue.format('YYYY-MM-DD'));
                                    }
                                }}
                                format="DD-MM-YYYY"
                                minDate={fromDate ? dayjs(fromDate) : dayjs()}
                                maxDate={dayjs().add(2, 'year')}
                                slots={{ calendarHeader: CustomCalendarHeader }}
                                slotProps={{
                                    toolbar: { hidden: true },
                                    textField: {
                                        fullWidth: true,
                                        size: isMobile ? "small" : "medium",
                                        autoComplete: 'off',
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ mr: -1 }}>
                                                    <ArrowDropDownIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                                                </InputAdornment>
                                            ),
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                cursor: "pointer",
                                                borderRadius: 2.5,
                                                backgroundColor: '#fff',
                                                '& fieldset': {
                                                    borderWidth: '2px',
                                                    borderColor: alpha('#F475A4', 0.15),
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: alpha('#F475A4', 0.3),
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#F475A4',
                                                },
                                                '& input': { cursor: 'pointer' }
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontWeight: 600
                                            }
                                        }
                                    }
                                }}
                            />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {/* From Time */}
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel id="duplicate-from-time-label">From Time</InputLabel>
                                <Select
                                    labelId="duplicate-from-time-label"
                                    value={fromTime}
                                    label="From Time"
                                    onChange={(e) => handleFromTimeChange(e.target.value as string)}
                                    sx={{
                                        borderRadius: 2.5,
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '2px',
                                            borderColor: alpha('#F475A4', 0.15),
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha('#F475A4', 0.3),
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#F475A4',
                                        }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                >
                                    {Array.from({ length: 24 }, (_, i) => {
                                        const time = `${String(i).padStart(2, '0')}:00`;
                                        const slotDt = dayjs(`${fromDate} ${time}`);
                                        const safeLimit = dayjs().add(2, 'hour');
                                        const isPast = slotDt.isBefore(safeLimit);
                                        return (
                                            <MenuItem key={time} value={time} disabled={isPast}>
                                                {time}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>

                            {/* To Time */}
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                <InputLabel id="duplicate-to-time-label">To Time</InputLabel>
                                <Select
                                    labelId="duplicate-to-time-label"
                                    value={toTime}
                                    label="To Time"
                                    onChange={(e) => setToTime(e.target.value as string)}
                                    // Disable manual change for fixed services
                                    disabled={isFixedService}
                                    sx={{
                                        borderRadius: 2.5,
                                        backgroundColor: isFixedService ? alpha('#F475A4', 0.05) : '#fff', // Visual cue for disabled state
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderWidth: '2px',
                                            borderColor: alpha('#F475A4', 0.15),
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: alpha('#F475A4', 0.3),
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#F475A4',
                                        }
                                    }}
                                    MenuProps={{ PaperProps: { sx: { maxHeight: 250 } } }}
                                >
                                    {Array.from({ length: 24 }, (_, i) => {
                                        const time = `${String(i).padStart(2, '0')}:00`;
                                        let isDisabled = false;

                                        if (fromDate === toDate) {
                                            const fromHour = parseInt(fromTime.split(':')[0], 10);
                                            const diff = i - fromHour;
                                            const effectiveDuration = diff < 0 ? diff + 24 : diff;
                                            if (effectiveDuration < 1) { // Min 1 hour duration
                                                isDisabled = true;
                                            }
                                        }

                                        return (
                                            <MenuItem key={time} value={time} disabled={isDisabled}>
                                                {time}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* Calculated Duration for Fixed Services */}
                        {isFixedService && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mt: -1.5, display: 'block' }}>
                                Duration: {fixedDurationMinutes / 60} hours (Fixed)
                            </Typography>
                        )}

                        {/* Overnight indicator */}
                        {toTime < fromTime && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#F475A4',
                                    fontWeight: 600,
                                    mt: -2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1
                                }}
                            >
                                <Box component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>•</Box>
                                Spans next day
                            </Typography>
                        )}
                    </LocalizationProvider>
                </Stack>
            </DialogContent>

            <Divider sx={{ borderColor: alpha('#F475A4', 0.1) }} />

            <DialogActions sx={{ p: 3.5, gap: 1.5 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    fullWidth={isMobile}
                    sx={{
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.2,
                        borderWidth: '2px',
                        borderColor: alpha('#F475A4', 0.3),
                        color: '#F475A4',
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#F475A4',
                            backgroundColor: alpha('#F475A4', 0.05)
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!fromDate || !toDate || !fromTime || !toTime}
                    fullWidth={isMobile}
                    sx={{
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.2,
                        background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                        boxShadow: '0 4px 16px rgba(244, 117, 164, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(244, 117, 164, 0.4)',
                            transform: 'translateY(-2px)'
                        }
                    }}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DuplicateModal;
